import React from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, TrendingUp, Users, Check, ArrowRight, Sparkles } from "lucide-react";
import Reveal from "@/components/marketing/Reveal";

const features = [
  { icon: Calendar, title: "Multi-venue calendar", desc: "Every venue gets its own colour and tab, with live booking-count badges." },
  { icon: TrendingUp, title: "Revenue tracking", desc: "Attach amounts to bookings; see month and FY-to-date totals update instantly." },
  { icon: Users, title: "Employee accounts", desc: "Invite staff to manage bookings while keeping your analytics private." },
];

export default function Products() {
  const navigate = useNavigate();
  return (
    <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-24">
      <Reveal>
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-cloud px-3.5 py-1.5 text-xs font-medium text-slate">
            <Sparkles size={13} className="text-pulse-green" /> Pulse Products
          </div>
          <h1 className="mt-6 text-5xl font-bold tracking-tight text-pulse-navy md:text-6xl">Built to grow with you.</h1>
          <p className="mt-5 text-lg text-slate">Pulse launches with the Calendar. The platform is architected so new products slot in without a rebuild.</p>
        </div>
      </Reveal>

      {/* Calendar product card */}
      <Reveal delay={0.1}>
        <div className="mt-14 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-pulse-md">
          <div className="grid lg:grid-cols-2">
            <div className="p-8 sm:p-12">
              <span className="rounded-md bg-pulse-green/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-pulse-green">Available now</span>
              <h2 className="mt-5 text-3xl font-bold tracking-tight text-pulse-navy">Calendar</h2>
              <p className="mt-4 text-slate">The complete booking and revenue system for venue owners. Book a date in seconds, track every rupee, and give your team exactly the access they need.</p>
              <ul className="mt-8 space-y-3">
                {["Book Date flow with client, amount & notes","Single-per-day rule with explicit override","Confirmed & tentative statuses","Edit / cancel with auto revenue adjustment","CSV export of bookings + revenue","Occupancy & analytics for owners"].map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-charcoal">
                    <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-pulse-green/10 text-pulse-green"><Check size={13} /></span>{f}
                  </li>
                ))}
              </ul>
              <button onClick={() => navigate("/pricing")} data-testid="products-trial-btn"
                className="mt-9 flex items-center gap-2 rounded-md bg-pulse-navy px-6 py-3.5 text-sm font-semibold text-white transition-transform hover:scale-[1.02] active:scale-[0.97]">
                Start free trial <ArrowRight size={16} />
              </button>
            </div>
            <div className="relative min-h-[320px] bg-pulse-navy p-6">
              <img src="https://images.unsplash.com/photo-1560461396-ec0ef7bb29dd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                   alt="Booking calendar" className="h-full w-full rounded-md object-cover shadow-pulse-lg" />
            </div>
          </div>
        </div>
      </Reveal>

      <div className="mt-10 grid gap-6 md:grid-cols-3">
        {features.map((f, i) => (
          <Reveal key={f.title} delay={0.1 + i * 0.08}>
            <div className="h-full rounded-xl border border-slate-200 bg-white p-7 transition-all duration-200 hover:-translate-y-1 hover:shadow-pulse-md">
              <div className="grid h-11 w-11 place-items-center rounded-md bg-pulse-navy/5 text-pulse-navy"><f.icon size={20} /></div>
              <h3 className="mt-5 text-lg font-semibold text-pulse-navy">{f.title}</h3>
              <p className="mt-2 text-sm text-slate">{f.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>

      {/* More products coming soon */}
      <Reveal delay={0.2}>
        <div className="relative mt-6 overflow-hidden rounded-xl border border-dashed border-slate-300 bg-cloud/40 p-8 text-center sm:p-10" data-testid="coming-soon">
          <div className="mx-auto grid h-11 w-11 place-items-center rounded-md bg-white shadow-pulse-sm"><Sparkles size={20} className="text-pulse-green" /></div>
          <h3 className="mt-4 text-xl font-semibold text-pulse-navy">More products are on the way</h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate">Pulse is built to grow. New tools for venue owners are coming soon — each one slots right in alongside your Calendar.</p>
          <span className="mt-4 inline-block rounded-full bg-pulse-navy px-3.5 py-1.5 text-xs font-semibold text-white">Coming soon</span>
        </div>
      </Reveal>
    </div>
  );
}
